import { FC, ReactNode, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { HugeRollingOffer } from '@appcharge/shared-ui';
import useOfferDesign from 'api/useOfferDesign';
import { Offer, RollingOfferProductSequence } from 'common/contracts';
import { ROLLING_OFFER_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';

import {
  ROLLING_CONTAINER_STYLE,
  STANDARD_PREVIEW_INDEX_TO_COLLECT
} from './RollingOfferPreview.consts';
import { mapProductsSequence } from './RollingOfferPreview.utils';

interface RollingOfferPreviewProps {
  currentOfferDesign: string;
  productsSequence: RollingOfferProductSequence[];
  progressBarOffers?: Offer[];
}

export const RollingOfferPreview: FC<RollingOfferPreviewProps> = ({
  currentOfferDesign,
  productsSequence,
  progressBarOffers = []
}) => {
  const { publisherId } = useParams();
  const { getOfferDesign } = useOfferDesign(publisherId, currentOfferDesign);

  const offerDesignData = getOfferDesign?.data;

  const subRollingOffersProducts = useMemo(
    () =>
      productsSequence
        ? mapProductsSequence(productsSequence, progressBarOffers)
        : [],
    [productsSequence, progressBarOffers]
  );

  return (
    <OfferPreviewShell containerStyle={ROLLING_CONTAINER_STYLE}>
      {({ storeScreen }): ReactNode => {
        if (!offerDesignData) return null;

        const rollingOffer = offerDesignData.rollingOffer;

        const subRollingOfferUiProps = {
          backgroundColor:
            normalizeBgColor(rollingOffer?.subRollingOffer?.backgroundColor) ||
            ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
          backgroundImage:
            rollingOffer?.subRollingOffer?.backgroundImage || undefined,
          lockImage:
            rollingOffer?.subRollingOffer?.lockImage ||
            ROLLING_OFFER_DEFAULTS.lockImage,
          buttonColor: storeScreen.buttonColor,
          collectText:
            rollingOffer?.subRollingOffer?.collectText ||
            ROLLING_OFFER_DEFAULTS.collectText,
          buttonTextColor: storeScreen.buttonTextColor
        };

        let backgroundOpacity = rollingOffer?.backgroundOpacity;
        if (backgroundOpacity === undefined) {
          backgroundOpacity = ROLLING_OFFER_DEFAULTS.backgroundOpacity;
        }

        let borderWidth = offerDesignData.borderWidth;
        if (borderWidth === undefined) {
          borderWidth = ROLLING_OFFER_DEFAULTS.borderWidth;
        }

        return (
          <HugeRollingOffer
            offerId="rolling-offer-preview"
            isPreview
            isPermanent
            backgroundImage={offerDesignData.backgroundImage}
            horizontalBackgroundImage={offerDesignData.horizontalBackgroundImage}
            headerImage={rollingOffer?.headerImage}
            backgroundOpacity={backgroundOpacity}
            backgroundColor={
              normalizeBgColor(rollingOffer?.backgroundColor) ||
              ROLLING_OFFER_DEFAULTS.backgroundColor
            }
            borderColor={
              normalizeBgColor(offerDesignData.borderColor) ||
              ROLLING_OFFER_DEFAULTS.borderColor
            }
            borderWidth={borderWidth}
            collectBehavior="disappear"
            subRollingOfferUiProps={subRollingOfferUiProps}
            selectOffer={() => undefined}
            indexToCollect={STANDARD_PREVIEW_INDEX_TO_COLLECT}
            arrowColor={
              rollingOffer?.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor
            }
            arrowImage={rollingOffer?.arrowImage || ''}
            subRollingOffersProducts={subRollingOffersProducts}
          />
        );
      }}
    </OfferPreviewShell>
  );
};
